/**
 * AnwaltsvollmachtFormular — Rechtsanwaltsvollmacht mit digitaler Unterschrift
 *
 * Verwendet im Kunden-Portal (KundenLinkPortal) wenn die Aktion
 * `unterschrift_anwaltsvollmacht` aktiv ist. Der Kunde liest die Vollmacht,
 * bestätigt eine Checkbox und unterschreibt digital. Das PDF wird generiert,
 * in Supabase Storage hochgeladen und via `completeAnwaltsvollmacht` in der
 * Akte gespeichert. (Issue #140)
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import SignaturePad from 'signature_pad';
import {
  Scale, Trash2, CheckCircle2, Pen,
  AlertCircle, Shield, ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import type { Kanzlei } from '@/lib/database.types';

// ── Types ──

export interface AnwaltsvollmachtFormData {
  vorname: string;
  nachname: string;
  strasse?: string;
  plz?: string;
  ort?: string;
  aktenzeichen?: string;
  kennzeichen?: string;
  schadendatum?: string;
  gegner_versicherung?: string;
  schadennummer?: string;
}

interface AnwaltsvollmachtFormularProps {
  kanzlei: Kanzlei | null;
  formData: AnwaltsvollmachtFormData;
  linkId: string;
  akteId?: string;
  onComplete: () => void;
  onBack: () => void;
}

const KOOP_ROT = '#C41E3A';

function formatDatum(d?: Date): string {
  const date = d || new Date();
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
}

export default function AnwaltsvollmachtFormular({
  kanzlei,
  formData,
  linkId,
  akteId,
  onComplete,
  onBack,
}: AnwaltsvollmachtFormularProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [vollmachtAccepted, setVollmachtAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unterschriftDatum, setUnterschriftDatum] = useState(formatDatum());

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(ratio, ratio);

    const pad = new SignaturePad(canvas, {
      backgroundColor: 'rgb(255, 255, 255)',
      penColor: 'rgb(0, 0, 0)',
      minWidth: 0.5,
      maxWidth: 2.5,
    });

    pad.addEventListener('endStroke', () => {
      setHasSignature(!pad.isEmpty());
    });

    signaturePadRef.current = pad;
    return () => { pad.off(); };
  }, []);

  const clearSignature = useCallback(() => {
    signaturePadRef.current?.clear();
    setHasSignature(false);
  }, []);

  const canSubmit = vollmachtAccepted && hasSignature;

  const kundeName = [formData.vorname, formData.nachname].filter(Boolean).join(' ') || '–';
  const kundeAdresse = [formData.strasse, [formData.plz, formData.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '–';

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error('Bitte akzeptieren Sie die Vollmacht und unterschreiben Sie.');
      return;
    }

    setIsSubmitting(true);
    try {
      const signatureDataUrl = signaturePadRef.current?.toDataURL('image/png') || '';
      const signatureTimestamp = new Date().toISOString();

      // PDF generieren
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      const contentWidth = pageWidth - 2 * margin;
      let y = 15;

      // Briefkopf
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text('Kfz Sachverstaendigenbuero Koop', margin, y);
      y += 6;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Inh. Andreas Koop - Kfz Techniker Meister & Kfz Sachverstaendiger', margin, y);
      y += 4;
      doc.text('Rahmedestrasse 65, 58507 Luedenscheid | Tel: 02351 / 87 69 860', margin, y);
      y += 2;

      doc.setDrawColor(196, 30, 58);
      doc.setLineWidth(0.8);
      doc.line(margin, y, pageWidth - margin, y);
      y += 8;

      // Titel
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text('Allgemeine Rechtsanwaltsvollmacht', margin, y);
      y += 10;

      // AZ / Kennzeichen
      if (formData.aktenzeichen || formData.kennzeichen) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80, 80, 80);
        const azLine = [
          formData.aktenzeichen ? `AZ: ${formData.aktenzeichen}` : null,
          formData.kennzeichen ? `KZ: ${formData.kennzeichen}` : null,
        ].filter(Boolean).join(' | ');
        doc.text(azLine, margin, y);
        y += 8;
      }

      // Vollmachtgeber
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text('Vollmachtgeber', margin, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.text(kundeName, margin, y); y += 4;
      doc.text(kundeAdresse, margin, y); y += 8;

      // Bevollmaechtigter
      doc.setFont('helvetica', 'bold');
      doc.text('Bevollmaechtigter (Rechtsanwaltskanzlei)', margin, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      if (kanzlei) {
        doc.text(kanzlei.name, margin, y); y += 4;
        if (kanzlei.adresse) { doc.text(kanzlei.adresse, margin, y); y += 4; }
        if (kanzlei.plz || kanzlei.ort) {
          doc.text(`${kanzlei.plz || ''} ${kanzlei.ort || ''}`.trim(), margin, y); y += 4;
        }
        if (kanzlei.telefon) { doc.text(`Tel: ${kanzlei.telefon}`, margin, y); y += 4; }
        if (kanzlei.email) { doc.text(`E-Mail: ${kanzlei.email}`, margin, y); y += 4; }
      } else {
        doc.text('Kanzlei nicht angegeben', margin, y); y += 4;
      }
      y += 4;

      // Vollmachtstext
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Vollmacht', margin, y); y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const vollmachtsText =
        `Hiermit erteile ich, ${kundeName}, der oben genannten Rechtsanwaltskanzlei Vollmacht, mich in allen Angelegenheiten betreffend meinen Schadenersatzanspruch aus dem Verkehrsunfall zu vertreten. Dies umfasst insbesondere die gerichtliche und aussergerichtliche Geltendmachung saemtlicher Ansprueche aus dem Unfallereignis, einschliesslich aller Nebenansprueche. Die Vollmacht erstreckt sich auf alle Instanzen und schliess Untervollmacht ein.`;
      const vLines = doc.splitTextToSize(vollmachtsText, contentWidth);
      doc.text(vLines, margin, y);
      y += vLines.length * 3.5 + 6;

      // Schadenfall-Details
      if (formData.schadendatum || formData.gegner_versicherung || formData.schadennummer) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Schadenfall', margin, y); y += 5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        if (formData.schadendatum) { doc.text(`Schadendatum: ${formData.schadendatum}`, margin, y); y += 4; }
        if (formData.gegner_versicherung) { doc.text(`Versicherung Gegner: ${formData.gegner_versicherung}`, margin, y); y += 4; }
        if (formData.schadennummer) { doc.text(`Schadennummer: ${formData.schadennummer}`, margin, y); y += 4; }
        y += 4;
      }

      // Datum + Unterschrift
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(unterschriftDatum, margin, y); y += 4;
      doc.text('Unterschrift Vollmachtgeber', margin, y); y += 2;
      doc.text(`(${kundeName})`, margin, y); y += 3;
      try {
        doc.addImage(signatureDataUrl, 'PNG', margin, y, 60, 25);
      } catch {
        // Fallback wenn Signatur-Bild nicht eingebettet werden kann
      }
      y += 30;

      doc.setFontSize(6);
      doc.setTextColor(150, 150, 150);
      doc.text(`Digital unterzeichnet am ${signatureTimestamp} | Gutachter KOOP App`, margin, 285);

      const pdfBlob = doc.output('blob');
      const pdfSize = pdfBlob.size;

      // Upload PDF in Supabase Storage — abort if upload fails (no broken-link DB entries)
      const fileName = `kunden-links/${linkId}/anwaltsvollmacht_${Date.now()}.pdf`;
      const { error: uploadError } = await supabase.storage
        .from('dokumente')
        .upload(fileName, pdfBlob, { contentType: 'application/pdf', upsert: true });

      if (uploadError) {
        console.error('[AnwaltsvollmachtFormular] Storage upload fehlgeschlagen:', uploadError);
        toast.error('Upload fehlgeschlagen. Bitte versuchen Sie es erneut.');
        setIsSubmitting(false);
        return;
      }

      // API-Call: Akte aktualisieren, Beteiligter anlegen, Aktion markieren
      const response = await fetch('/api/kunden-links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'completeAnwaltsvollmacht',
          link_id: linkId,
          storage_pfad: fileName,
          groesse_bytes: pdfSize,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Speichern fehlgeschlagen');
      }

      // PDF-Download anbieten
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Anwaltsvollmacht_${formData.nachname || 'Vollmacht'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Vollmacht unterzeichnet und gespeichert!');
      onComplete();
    } catch (err) {
      console.error('[AnwaltsvollmachtFormular] Fehler:', err);
      toast.error('Fehler beim Speichern der Vollmacht');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <ArrowLeft className="w-4 h-4 text-gray-500" />
        </button>
        <div className="flex-1">
          <h2 className="text-[17px] font-bold text-gray-900">Anwaltsvollmacht</h2>
          <p className="text-[12px] text-gray-500">Vollmacht für eine Rechtsanwaltskanzlei</p>
        </div>
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shadow"
          style={{ background: `linear-gradient(135deg, ${KOOP_ROT}, #9B1627)` }}
        >
          <Scale size={18} className="text-white" />
        </div>
      </div>

      {/* Kanzlei-Info */}
      {kanzlei && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-[11px] font-bold text-blue-700 uppercase tracking-wider mb-1.5">Bevollmächtigte Kanzlei</p>
          <p className="text-[14px] font-bold text-blue-900">{kanzlei.name}</p>
          {kanzlei.adresse && <p className="text-[12px] text-blue-700">{kanzlei.adresse}</p>}
          {(kanzlei.plz || kanzlei.ort) && (
            <p className="text-[12px] text-blue-700">{kanzlei.plz} {kanzlei.ort}</p>
          )}
          {kanzlei.telefon && <p className="text-[12px] text-blue-600 mt-1">Tel: {kanzlei.telefon}</p>}
        </div>
      )}

      {/* Dokument */}
      <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm overflow-hidden">
        <div className="max-h-[40vh] overflow-y-auto p-5 space-y-4" style={{ WebkitOverflowScrolling: 'touch' }}>

          {/* Briefkopf */}
          <div className="border-b-2 pb-3" style={{ borderColor: KOOP_ROT }}>
            <h3 className="text-[14px] font-bold text-gray-900">Kfz Sachverstaendigenbuero Koop</h3>
            <p className="text-[10px] text-gray-500">Inh. Andreas Koop · Rahmedestr. 65, 58507 Luedenscheid</p>
          </div>

          <h4 className="text-[13px] font-bold text-gray-900 text-center">Allgemeine Rechtsanwaltsvollmacht</h4>

          {/* Vollmachtgeber / Bevollmaechtigter */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px]">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="font-bold text-gray-700 mb-1">Vollmachtgeber</p>
              <p className="text-gray-700">{kundeName}</p>
              <p className="text-gray-500">{kundeAdresse}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="font-bold text-gray-700 mb-1">Bevollmächtigte Kanzlei</p>
              {kanzlei ? (
                <>
                  <p className="text-gray-700 font-medium">{kanzlei.name}</p>
                  {kanzlei.adresse && <p className="text-gray-500">{kanzlei.adresse}</p>}
                  {(kanzlei.plz || kanzlei.ort) && <p className="text-gray-500">{kanzlei.plz} {kanzlei.ort}</p>}
                </>
              ) : (
                <p className="text-gray-400 italic">Keine Kanzlei ausgewählt</p>
              )}
            </div>
          </div>

          {/* Vollmachtstext */}
          <div>
            <h5 className="text-[12px] font-bold text-gray-800 mb-1.5">Vollmacht</h5>
            <p className="text-[10px] text-gray-600 leading-relaxed">
              Hiermit erteile ich, <strong>{kundeName}</strong>, der oben genannten Rechtsanwaltskanzlei
              Vollmacht, mich in allen Angelegenheiten betreffend meinen Schadenersatzanspruch aus dem
              Verkehrsunfall zu vertreten. Dies umfasst insbesondere die gerichtliche und aussergerichtliche
              Geltendmachung saemtlicher Ansprueche aus dem Unfallereignis, einschliesslich aller
              Nebenansprueche. Die Vollmacht erstreckt sich auf alle Instanzen und schliesst Untervollmacht ein.
            </p>
          </div>

          {/* Schadenfall-Details */}
          {(formData.schadendatum || formData.gegner_versicherung || formData.schadennummer || formData.kennzeichen) && (
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-[11px] font-bold text-gray-700 mb-1.5">Schadenfall</p>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                {formData.kennzeichen && (
                  <><dt className="text-gray-400">Kennzeichen</dt><dd className="text-gray-700 font-medium">{formData.kennzeichen}</dd></>
                )}
                {formData.schadendatum && (
                  <><dt className="text-gray-400">Schadendatum</dt><dd className="text-gray-700 font-medium">{formData.schadendatum}</dd></>
                )}
                {formData.gegner_versicherung && (
                  <><dt className="text-gray-400">Versicherung</dt><dd className="text-gray-700 font-medium">{formData.gegner_versicherung}</dd></>
                )}
                {formData.schadennummer && (
                  <><dt className="text-gray-400">Schadennr.</dt><dd className="text-gray-700 font-medium">{formData.schadennummer}</dd></>
                )}
              </dl>
            </div>
          )}
        </div>
      </div>

      {/* Checkbox */}
      <button
        onClick={() => setVollmachtAccepted(!vollmachtAccepted)}
        className={`w-full flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all ${
          vollmachtAccepted ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 bg-white hover:border-gray-300'
        }`}
      >
        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
          vollmachtAccepted ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300'
        }`}>
          {vollmachtAccepted && <CheckCircle2 size={12} className="text-white" />}
        </div>
        <span className="text-[11px] text-gray-700 leading-relaxed">
          Ich erteile hiermit die obenstehende Vollmacht und bestätige, dass ich den Inhalt gelesen und verstanden habe.
        </span>
      </button>

      {/* Unterschriften-Pad */}
      <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Pen size={14} className="text-gray-500" />
            <span className="text-[12px] font-bold text-gray-700">Unterschrift</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={unterschriftDatum}
              onChange={e => setUnterschriftDatum(e.target.value)}
              className="text-[11px] text-gray-500 bg-transparent border-b border-dashed border-gray-300 focus:outline-none focus:border-gray-500 w-36 text-center"
            />
            {hasSignature && (
              <button
                onClick={clearSignature}
                className="flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-[10px] font-semibold transition-all"
              >
                <Trash2 size={12} /> Löschen
              </button>
            )}
          </div>
        </div>
        <div className="relative">
          <canvas
            ref={canvasRef}
            className="w-full touch-none"
            style={{ height: '130px', cursor: 'crosshair' }}
          />
          {!hasSignature && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <p className="text-[13px] text-gray-300 font-medium">Hier unterschreiben</p>
            </div>
          )}
        </div>
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
          <p className="text-[10px] text-gray-400">Unterschrift Vollmachtgeber ({kundeName})</p>
        </div>
      </div>

      {/* DSGVO Hinweis */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-3">
        <Shield size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
        <p className="text-[11px] text-blue-700 leading-relaxed">
          <strong>Rechtlicher Hinweis:</strong> Ihre digitale Unterschrift wird mit Zeitstempel gespeichert.
          Die unterzeichnete Vollmacht wird als PDF in Ihrer Akte hinterlegt.
        </p>
      </div>

      {/* Buttons */}
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600"
        >
          Zurück
        </button>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          className="flex items-center gap-2 px-6 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: `linear-gradient(135deg, ${KOOP_ROT}, #9B1627)` }}
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Wird erstellt...
            </>
          ) : (
            <>
              <CheckCircle2 size={16} />
              Vollmacht erteilen
            </>
          )}
        </button>
      </div>

      {!canSubmit && (vollmachtAccepted || hasSignature) && (
        <div className="flex items-center gap-2 justify-center">
          <AlertCircle size={12} className="text-amber-500" />
          <p className="text-[11px] text-amber-600">
            {!hasSignature ? 'Bitte unterschreiben Sie im Feld oben' : 'Bitte bestätigen Sie die Vollmacht'}
          </p>
        </div>
      )}
    </div>
  );
}
